import classNames from 'classnames';
import { useConfigContext } from 'contexts/ConfigContext';
import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { WizardSlot } from 'layout/wizard/slots';
import { Form, PartnerForm, TabList } from '@portal/ui-library';
import { useApplicationHelpers } from 'hooks/useApplicationHelpers';
import { applicationField, partnerField } from 'models';
import { partnerFormConfig } from 'utils/partnerFormConfig';
import { HealthQuestionsForm } from 'pages/DigitalOrderPage/components/HealthQuestionsForm';
import { useTranslation } from 'react-i18next';
import { TariffSection } from '../TariffSection';
import { HealthDisclaimer } from './HealthDisclaimer/HealthDisclaimer';
import styles from './index.module.scss';

import { Partner, PersonRoleEnum } from 'models/extension-generated';
import { NavigationSection } from 'pages/DigitalOrderPage/components/NavigationSection/NavigationSection';
import { useRef } from 'react';
import Typography from '@mui/material/Typography';
import { Tab, Tabs } from '@mui/material';

const getPartnerKeys = (partnerId: string) => ({
  // Personal Data
  // InsuredPersonForm
  genderKey: partnerField(partnerId, 'gender'),
  salutationKey: partnerField(partnerId, 'salutation'),
  titleKey: partnerField(partnerId, 'title'),
  firstNameKey: partnerField(partnerId, 'firstname'),
  lastNameKey: partnerField(partnerId, 'lastname'),
  birthDateKey: partnerField(partnerId, 'birthDate'),
  fundsFromGZKey: partnerField(partnerId, 'applicationInformation.fundsFromGZ'),
  employmentTypeKey: partnerField(partnerId, 'applicationInformation.employmentGroup'),
  professionKey: partnerField(partnerId, 'applicationInformation.profession'),
  // InsuranceHolderRelation
  relationshipKey: partnerField(partnerId, 'applicationInformation.relationshipToHolder'),
  // GermanAddressForm
  livingInGermanyKey: partnerField(partnerId, 'livingInGermany'),
  // AddressForm
  streetKey: partnerField(partnerId, 'permanentResidence.street'),
  houseNumberKey: partnerField(partnerId, 'permanentResidence.houseNumber'),
  postalCodeKey: partnerField(partnerId, 'permanentResidence.postalCode'),
  cityKey: partnerField(partnerId, 'permanentResidence.city'),
  // ForeignAddressForm
  hasForeignResidenceKey: partnerField(partnerId, 'hasLivedInForeignCountry'),
  foreignCountryKey: partnerField(partnerId, 'foreignCountry'),
  // Health
  hasLegalObligationToNotify: partnerField(
    partnerId,
    'applicationInformation.hasLegalObligationToNotify'
  ),
  // Application
  applicationStartKey: applicationField('applicationStart')
});

export function InsuredPersonStep() {
  const { t } = useTranslation('insuredPersonStep');

  const mainRef = useRef<HTMLDivElement>(null);
  const { config: stateConfig } = useConfigContext();
  const { state, dispatch, partnerId, selectPartner } = useHealthInsuranceContext();
  const { getPartnersByRole } = useApplicationHelpers(state.model);
  const partnerKeys = getPartnerKeys(partnerId);
  const insuredPartners = getPartnersByRole(PersonRoleEnum.INSURED_PERSON);

  const tabGroupOptions: Array<TabList> = insuredPartners.map((partner) => {
    const hasPartnerError = (partner: Partner): boolean => {
      return (
        Array.from(state.validationResults.keys()).filter((key) => key.includes(partner.id!))
          .length > 0
      );
    };
    const hasError = hasPartnerError(partner);

    const name = (() => {
      if (!partner.lastname && !partner.firstname) return t('Neue versicherte Person');
      let nameText = '';
      if (partner.lastname) nameText += partner.lastname;
      if (partner.lastname && partner.firstname) nameText += ', ';
      if (partner.firstname) nameText += partner.firstname;

      return nameText;
    })();
    return {
      id: partner.id || '',
      counter: 0,
      label: (
        <Typography
          variant={partner.id === partnerId ? 'bodyMDBold' : 'bodyMDRegular'}
          className={classNames({
            ['text-error-700']: hasError
          })}
        >
          {name}
        </Typography>
      )
    };
  });

  return (
    <WizardSlot.Main ref={mainRef}>
      {insuredPartners.length > 1 && (
        <div className={'py-m px-[8px] s:px-[80px] bg-white-100'}>
          <>
            <Tabs
              value={partnerId}
              onChange={(_, id) => selectPartner(String(id))}
              variant="scrollable"
              scrollButtons="auto"
            >
              {tabGroupOptions.map((tab, index) => {
                return (
                  <Tab
                    autoFocus={tab.id === partnerId}
                    key={tab.id}
                    aria-description={t('insuredPersonStep:personTabDescription')}
                    value={tab.id}
                    label={tab.label}
                    className={classNames({
                      ['border-b-navigation-80 border-b']: tab.id === partnerId
                    })}
                    sx={{
                      minWidth: '200px',
                      width: '250px'
                    }}
                  />
                );
              })}
            </Tabs>
          </>
        </div>
      )}
      <Form state={state} dispatch={dispatch}>
        <TariffSection
          partnerKeys={{
            birthDateKey: partnerKeys.birthDateKey,
            applicationStartKey: partnerKeys.applicationStartKey
          }}
        />
        <fieldset className={classNames('bg-white-100', styles.questionWrapperInner)}>
          <legend className="mx-[16px]">
            <Typography
              component={'h2'}
              aria-describedby="Alle Felder, die im Namen ein * halten, sind Pflichtfelder"
              variant="headlineXXLGBold"
              className="text-navigation-80 text-center s:text-left m-[0px] py-m"
            >
              {t('insuredPeople.steps.personalData.title', { ns: 'wizardPrivateHealthInsurance' })}
            </Typography>
          </legend>
          <PartnerForm
            config={partnerFormConfig}
            partnerId={partnerId}
            hasEmployment={{
              showAdditionalEmploymentQuestions: false
            }}
            hasPersonalData
            hasAddress
            hasForeignAddress
            hasHolderRelation
          />
        </fieldset>

        <div className="s:mx-[80px] ">
          <div className="grid-mx-res my-s s:my-m">
            <Typography
              component={'h2'}
              id="health-questions-title"
              variant="headlineXXLGBold"
              className="text-navigation-80 "
            >
              {t('insuredPeople.steps.health.title', { ns: 'wizardPrivateHealthInsurance' })}
            </Typography>
          </div>

          <div className="grid-res" aria-labelledby="health-questions-title">
            <HealthDisclaimer componentKey={partnerKeys.hasLegalObligationToNotify} />
          </div>

          <div className="grid-mx-res">
            <HealthQuestionsForm partnerId={partnerId} config={stateConfig} state={state} />
          </div>
        </div>
        <div className="s:mx-[80px] text-[12px] font-[400] leading-[18px] tracking-normal text-text-60">
          {t('common:fieldRequired')}
        </div>
      </Form>
      <NavigationSection
        onPersonSwitch={() => {
          setTimeout(() => {
            mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          }, 500);
        }}
      />
    </WizardSlot.Main>
  );
}
